"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// ── Tiny helpers ──────────────────────────────────────────────────────────────
function Badge({ children, color = "gray" }) {
  const map = {
    gray: "bg-gray-700 text-gray-300",
    blue: "bg-blue-900/60 text-blue-300",
    orange: "bg-orange-900/60 text-orange-300",
    purple: "bg-purple-900/60 text-purple-300",
    green: "bg-green-900/60 text-green-300",
    yellow: "bg-yellow-900/60 text-yellow-200",
  };
  return (
    <span
      className={cls(
        "px-1.5 py-0.5 text-[10px] font-semibold rounded",
        map[color] || map.gray,
      )}
    >
      {children}
    </span>
  );
}

function Spinner({ size = 4 }) {
  return (
    <span
      className={`animate-spin inline-block w-${size} h-${size} border-2 border-white/30 border-t-white rounded-full`}
    />
  );
}

// ── Section pill ──────────────────────────────────────────────────────────────
function SectionPill({ sec }) {
  return (
    <span className="text-[9px] font-mono text-indigo-400 bg-indigo-900/40 px-1.5 py-0.5 rounded whitespace-nowrap">
      §{sec}
    </span>
  );
}

// ── Item row inside the PO item table ─────────────────────────────────────────
function ItemRow({ item, isSelected, onToggle, targetPOs, onMove, moving }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={cls(
          "transition-colors",
          isSelected ? "bg-orange-900/20" : "hover:bg-gray-700/30",
        )}
      >
        {/* Checkbox */}
        <td className="px-2 py-2 text-center w-8">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(item._id)}
            className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
          />
        </td>

        {/* Expand toggle */}
        <td className="px-1 py-2 w-5 text-center">
          <button
            onClick={() => setExpanded((p) => !p)}
            className={cls(
              "text-[10px] transition-transform",
              expanded
                ? "rotate-90 text-orange-400"
                : "text-gray-600 hover:text-gray-400",
            )}
          >
            ▶
          </button>
        </td>

        {/* Source badge */}
        <td className="px-2 py-2">
          {item.source === "change_order" ? (
            <Badge color="purple">CO</Badge>
          ) : item.source === "manual" ? (
            <Badge color="yellow">MAN</Badge>
          ) : (
            <Badge color="blue">BUD</Badge>
          )}
        </td>

        {/* Section */}
        <td className="px-2 py-2">
          <SectionPill sec={item.sectionNumber || "—"} />
        </td>

        {/* Code */}
        <td className="px-2 py-2 font-mono text-orange-400 text-xs">
          {item.itemCode || "—"}
        </td>

        {/* Description */}
        <td className="px-2 py-2 text-gray-200 text-xs max-w-[220px]">
          <span className="line-clamp-1">
            {item.costCodeDescription || item.itemDescription || "—"}
          </span>
        </td>

        {/* Size */}
        <td className="px-2 py-2 text-gray-400 text-xs whitespace-nowrap">
          {item.size || "—"}
        </td>
        <td className="px-2 py-2 text-gray-400 text-xs whitespace-nowrap">
          {item.bidArea || "—"}
        </td>

        {/* Qty */}
        <td className="px-2 py-2 text-right text-gray-300 font-mono text-xs">
          {(item.requiredQty ?? item.quantity ?? 0).toLocaleString("en-US", {
            maximumFractionDigits: 2,
          })}
        </td>

        {/* UOM */}
        <td className="px-2 py-2 text-center text-gray-400 text-xs">
          {item.uom || item.container || "—"}
        </td>

        {/* Unit cost */}
        <td className="px-2 py-2 text-right text-gray-300 font-mono text-xs">
          ${fmt(item.unitCost || item.price || 0)}
        </td>

        {/* Amount */}
        <td className="px-2 py-2 text-right font-mono font-semibold text-blue-300 text-xs">
          ${fmt(item.amount || 0)}
        </td>

        {/* Move control */}
        <td className="px-2 py-2 text-right w-36">
          {targetPOs.length > 0 ? (
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  onMove([item._id], e.target.value);
                  e.target.value = "";
                }
              }}
              disabled={moving}
              className="px-1.5 py-0.5 text-[10px] bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none w-full disabled:opacity-50"
            >
              <option value="">Move to…</option>
              {targetPOs.map((po) => (
                <option key={po._id} value={po._id}>
                  {po.title}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-[10px] text-gray-600 italic">Only PO</span>
          )}
        </td>
      </tr>

      {/* Expanded detail */}
      {expanded && (
        <tr className="bg-gray-900/50">
          <td
            colSpan={12}
            className="px-8 py-2 text-[11px] text-gray-400 space-y-0.5"
          >
            <div className="flex flex-wrap gap-x-6 gap-y-0.5">
              <span>
                Bid Area:{" "}
                <span className="text-gray-200">{item.bidArea || "—"}</span>
              </span>
              <span>
                Mat Code:{" "}
                <span className="text-gray-200 font-mono">
                  {item.matCode || "—"}
                </span>
              </span>
              <span>
                Labor Code:{" "}
                <span className="text-gray-200 font-mono">
                  {item.laborCode || "—"}
                </span>
              </span>
              <span>
                Coverage/Unit:{" "}
                <span className="text-gray-200">
                  {item.coveragePerUnit || "—"}
                </span>
              </span>
              <span>
                Category:{" "}
                <span className="text-gray-200">{item.category || "—"}</span>
              </span>
              {item.source === "change_order" && (
                <span>
                  CO Ref:{" "}
                  <span className="text-purple-300 font-semibold">
                    {item.changeOrderRef || "—"}
                  </span>
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── PO accordion card ─────────────────────────────────────────────────────────
function POCard({
  po,
  allPOs,
  token,
  projectId,
  onRename,
  onItemsMoved,
  onDeleted,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Rename state
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(po.title);
  const [renameSaving, setRenameSaving] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [moving, setMoving] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [secFilter, setSecFilter] = useState("all");
  const [srcFilter, setSrcFilter] = useState("all");

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bidFilter, setBidFilter] = useState("all");
  const headers = { Authorization: `Bearer ${token}` };

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const res = await axios.get(
        `${API}/purchaseOrders3/${po._id}/items?limit=1000`,
        { headers },
      );
      setItems(res.data.items || []);
      setItemsLoaded(true);
    } catch (err) {
      console.error("Failed to load PO items:", err.message);
    } finally {
      setItemsLoading(false);
    }
  }, [po._id, token]);

  const uniqueBidAreas = useMemo(
    () =>
      [
        "all",
        ...new Set(items.map((i) => i.bidArea).filter(Boolean)).values(),
      ].sort(),
    [items],
  );

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !itemsLoaded) loadItems();
  };

  const handleRename = async () => {
    const val = renameVal.trim();
    if (!val || val === po.title) {
      setRenaming(false);
      return;
    }
    setRenameSaving(true);
    try {
      await axios.put(
        `${API}/purchaseOrders3/${po._id}`,
        { title: val },
        { headers },
      );
      onRename(po._id, val);
      setRenaming(false);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setRenameSaving(false);
    }
  };

  const handleMoveSelected = async (targetPoId) => {
    if (!selected.size) return;
    await handleMove([...selected], targetPoId);
    setSelected(new Set());
  };

  const handleMove = async (itemIds, targetPoId) => {
    setMoving(true);
    try {
      await axios.patch(
        `${API}/purchaseOrders3/${po._id}/items/move`,
        { itemIds, targetPoId },
        { headers },
      );
      setItems((prev) => prev.filter((i) => !itemIds.includes(i._id)));
      // reset filters so results aren't stuck empty
      setSearch("");
      setSecFilter("all");
      setSrcFilter("all");
      setBidFilter("all");
      setSelected(new Set());
      onItemsMoved(po._id, targetPoId, itemIds);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setMoving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/purchaseOrders3/${po._id}`, { headers });
      onDeleted(po._id);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const toggleItem = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map((i) => i._id)));
  };

  const uniqueSections = useMemo(
    () =>
      [
        "all",
        ...new Set(items.map((i) => i.sectionNumber).filter(Boolean)).values(),
      ].sort(),
    [items],
  );

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((item) => {
      const matchSec = secFilter === "all" || item.sectionNumber === secFilter;
      const matchBid = bidFilter === "all" || item.bidArea === bidFilter;
      const matchSrc = srcFilter === "all" || item.source === srcFilter;
      const matchQ =
        !q ||
        (item.costCodeDescription || item.itemDescription || "")
          .toLowerCase()
          .includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.size || "").toLowerCase().includes(q);
      return matchSec && matchSrc && matchBid && matchQ;
    });
  }, [items, search, secFilter, srcFilter, bidFilter]);

  const totals = useMemo(
    () => filteredItems.reduce((a, i) => a + (i.amount || 0), 0),
    [filteredItems],
  );

  const targetPOs = allPOs.filter((p) => p._id !== po._id);

  const hasCO = items.some((i) => i.source === "change_order");
  const hasManual = items.some((i) => i.source === "manual");

  return (
    <div
      className={cls(
        "rounded-xl border overflow-hidden transition-all",
        open
          ? "border-orange-600/50 bg-gray-800"
          : "border-gray-700 bg-gray-800",
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Collapse toggle */}
        <button
          onClick={handleToggle}
          className={cls(
            "text-[11px] transition-transform shrink-0",
            open
              ? "rotate-90 text-orange-400"
              : "text-gray-500 hover:text-gray-300",
          )}
        >
          ▶
        </button>

        {/* PO number badge */}
        <span className="text-[10px] font-mono text-gray-500 shrink-0">
          #{po.poNumber}
        </span>

        {/* Title / rename */}
        {renaming ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") {
                  setRenaming(false);
                  setRenameVal(po.title);
                }
              }}
              className="flex-1 px-2 py-1 text-sm bg-gray-700 border border-orange-500 rounded text-gray-100 focus:outline-none min-w-0"
            />
            <button
              onClick={handleRename}
              disabled={renameSaving}
              className="px-2.5 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold disabled:opacity-50 flex items-center gap-1"
            >
              {renameSaving ? <Spinner size={3} /> : "Save"}
            </button>
            <button
              onClick={() => {
                setRenaming(false);
                setRenameVal(po.title);
              }}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white border border-gray-600 rounded"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm font-semibold text-white truncate">
              {po.title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setRenaming(true);
              }}
              className="shrink-0 text-[10px] text-gray-500 hover:text-orange-400 transition-colors"
              title="Rename PO"
            >
              ✏
            </button>
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {po.bidArea && (
            <span className="text-[10px] text-gray-500 hidden sm:block">
              {po.bidArea}
            </span>
          )}
          <span
            className={cls(
              "text-[10px] px-1.5 py-0.5 rounded font-semibold",
              po.status === "active"
                ? "bg-green-900/50 text-green-300"
                : "bg-gray-700 text-gray-400",
            )}
          >
            {po.status}
          </span>
          <span className="text-sm font-bold text-green-300 font-mono">
            ${fmt(po.originalEstimatedAmount || 0)}
          </span>
          <span className="text-xs text-gray-500">
            {po.sectionCount > 0 ? `${po.sectionCount} sec` : ""}
          </span>

          {/* Delete */}
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-[10px] text-gray-600 hover:text-red-400 transition-colors ml-1"
              title="Delete PO"
            >
              🗑
            </button>
          ) : (
            <div className="flex items-center gap-1 bg-red-950 border border-red-700 rounded px-2 py-0.5">
              <span className="text-[10px] text-red-300">Delete?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="text-[10px] text-red-300 hover:text-red-100 font-semibold disabled:opacity-50"
              >
                {deleting ? <Spinner size={3} /> : "Yes"}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] text-gray-400 hover:text-white"
              >
                No
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Expanded body ── */}
      {open && (
        <div className="border-t border-gray-700">
          {itemsLoading ? (
            <div className="flex items-center justify-center py-8 gap-2">
              <Spinner />
              <span className="text-xs text-gray-400">Loading items…</span>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-gray-700/60 bg-gray-900/30">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search items…"
                  className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-40"
                />
                <select
                  value={secFilter}
                  onChange={(e) => setSecFilter(e.target.value)}
                  className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
                >
                  {uniqueSections.map((s) => (
                    <option key={s} value={s}>
                      {s === "all" ? "All Sections" : `§${s}`}
                    </option>
                  ))}
                </select>
                {uniqueBidAreas.length > 2 && (
                  <select
                    value={bidFilter}
                    style={{ WebkitAppearance: "none", appearance: "none" }}
                    onChange={(e) => setBidFilter(e.target.value)}
                    className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
                  >
                    {uniqueBidAreas.map((b) => (
                      <option key={b} value={b}>
                        {b === "all" ? "All Bid Areas" : b}
                      </option>
                    ))}
                  </select>
                )}
                {/* Source filter */}
                <div className="flex rounded overflow-hidden border border-gray-600">
                  {[
                    { v: "all", l: "All" },
                    { v: "budget", l: "Bud" },
                    ...(hasCO ? [{ v: "change_order", l: "CO" }] : []),
                    ...(hasManual ? [{ v: "manual", l: "Man" }] : []),
                  ].map(({ v, l }) => (
                    <button
                      key={v}
                      onClick={() => setSrcFilter(v)}
                      className={cls(
                        "px-2 py-1 text-[10px] font-medium transition-colors",
                        srcFilter === v
                          ? "bg-orange-500 text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-gray-500">
                  {filteredItems.length} / {items.length} items
                </span>
                {(search ||
                  secFilter !== "all" ||
                  srcFilter !== "all" ||
                  bidFilter !== "all") && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setSecFilter("all");
                      setSrcFilter("all");
                      setBidFilter("all");
                    }}
                    className="text-[10px] text-orange-400 hover:text-orange-300 underline"
                  >
                    Clear filters
                  </button>
                )}

                <span className="text-xs font-mono font-bold text-blue-300 ml-auto">
                  ${fmt(totals)}
                </span>
              </div>

              {/* Bulk action bar */}
              {selected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 bg-orange-950/40 border-b border-orange-700/40">
                  <span className="text-xs text-orange-300 font-semibold">
                    {selected.size} item{selected.size !== 1 ? "s" : ""}{" "}
                    selected
                  </span>
                  {targetPOs.length > 0 && (
                    <>
                      <span className="text-xs text-gray-400">Move to:</span>
                      {targetPOs.map((tpo) => (
                        <button
                          key={tpo._id}
                          onClick={() => handleMoveSelected(tpo._id)}
                          disabled={moving}
                          className="px-2.5 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded border border-gray-600 disabled:opacity-50 flex items-center gap-1"
                        >
                          {moving && <Spinner size={3} />}
                          {tpo.title}
                        </button>
                      ))}
                    </>
                  )}
                  <button
                    onClick={() => setSelected(new Set())}
                    className="ml-auto text-[10px] text-gray-500 hover:text-white"
                  >
                    ✕ Clear
                  </button>
                </div>
              )}

              {/* Items table */}
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900/80 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={
                            filteredItems.length > 0 &&
                            selected.size === filteredItems.length
                          }
                          onChange={toggleAll}
                          className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
                        />
                      </th>
                      <th className="w-5" />
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Type
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Sec
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Code
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium min-w-[180px]">
                        Description
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Size
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Bid Area
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-center text-gray-500 font-medium">
                        UOM
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Unit $
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Amount
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium w-36">
                        Move
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/40">
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          className="px-4 py-8 text-center text-gray-500 italic"
                        >
                          No items match your filters.
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                        <ItemRow
                          key={item._id}
                          item={item}
                          isSelected={selected.has(item._id)}
                          onToggle={toggleItem}
                          targetPOs={targetPOs}
                          onMove={handleMove}
                          moving={moving}
                        />
                      ))
                    )}
                  </tbody>
                  {filteredItems.length > 0 && (
                    <tfoot className="sticky bottom-0 bg-gray-900/90 border-t border-gray-700">
                      <tr>
                        <td
                          colSpan={11}
                          className="px-2 py-2 text-xs text-gray-500"
                        >
                          {filteredItems.length} items shown
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-bold text-blue-300">
                          ${fmt(totals)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main panel
// ═════════════════════════════════════════════════════════════════════════════
export default function EditPOsPanel({
  projectId,
  token,
  onClose,
  onSaved,
  initialCreate = false,
}) {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // New-PO creation
  const [showCreate, setShowCreate] = useState(initialCreate);
  const [newTitle, setNewTitle] = useState("");
  const [newBidArea, setNewBidArea] = useState("");
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/purchaseOrders3/project/${projectId}`,
        { headers },
      );
      setPOs(res.data.purchaseOrders || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRename = (poId, newTitle) => {
    setPOs((prev) =>
      prev.map((p) => (p._id === poId ? { ...p, title: newTitle } : p)),
    );
    onSaved?.();
  };

  const handleItemsMoved = (fromId, toId, itemIds) => {
    // Refresh both POs after move
    onSaved?.();
  };

  const handleDeleted = (poId) => {
    setPOs((prev) => prev.filter((p) => p._id !== poId));
    onSaved?.();
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/purchaseOrders3/project/${projectId}`,
        { title: newTitle.trim(), bidArea: newBidArea.trim() },
        { headers },
      );
      setPOs((prev) => [...prev, res.data.purchaseOrder]);
      setNewTitle("");
      setNewBidArea("");
      setShowCreate(false);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setCreating(false);
    }
  };

  const filteredPOs = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return pos;
    return pos.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.bidArea || "").toLowerCase().includes(q) ||
        (p.poNumber || "").toString().includes(q),
    );
  }, [pos, search]);

  const grandTotal = useMemo(
    () => pos.reduce((s, p) => s + (p.originalEstimatedAmount || 0), 0),
    [pos],
  );

  return (
    /* ── Full-screen overlay ── */
    <div className="fixed inset-0 z-[99999] flex flex-col bg-gray-950/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-700 bg-gray-900 shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-white">
            Edit Purchase Orders
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {pos.length} PO{pos.length !== 1 ? "s" : ""} · Grand total:{" "}
            <span className="text-green-300 font-semibold font-mono">
              ${fmt(grandTotal)}
            </span>
          </p>
        </div>

        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter POs…"
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-48"
        />

        {/* New PO button */}
        <button
          onClick={() => setShowCreate((p) => !p)}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          + New PO
        </button>

        {/* Close */}
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
        >
          ✕ Close
        </button>
      </div>

      {/* Create form (collapsible) */}
      {showCreate && (
        <div className="shrink-0 px-6 py-4 border-b border-gray-700 bg-gray-800/60">
          <div className="flex flex-wrap items-end gap-3 max-w-2xl">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[10px] text-gray-400 mb-1">
                PO Title *
              </label>
              <input
                autoFocus
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="e.g. Level 3 — Drywall"
                className="w-full px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div className="w-36">
              <label className="block text-[10px] text-gray-400 mb-1">
                Bid Area / Floor
              </label>
              <input
                type="text"
                value={newBidArea}
                onChange={(e) => setNewBidArea(e.target.value)}
                placeholder="e.g. L3"
                className="w-full px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || creating}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? <Spinner size={3} /> : null}
              Create
            </button>
            <button
              onClick={() => {
                setShowCreate(false);
                setNewTitle("");
                setNewBidArea("");
              }}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Spinner />
            <span className="text-gray-400">Loading purchase orders…</span>
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-400">{error}</div>
        ) : filteredPOs.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {pos.length === 0
              ? "No purchase orders found."
              : "No POs match your filter."}
          </div>
        ) : (
          filteredPOs.map((po) => (
            <POCard
              key={po._id}
              po={po}
              allPOs={pos}
              token={token}
              projectId={projectId}
              onRename={handleRename}
              onItemsMoved={handleItemsMoved}
              onDeleted={handleDeleted}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-3 border-t border-gray-700 bg-gray-900 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          💡 Click ▶ on any PO to expand its items. Use checkboxes to bulk-move
          items between POs.
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
